import styled from 'styled-components';

import { alpha, Stack } from '@mui/material';
import { ElementRef, useRef, useState } from 'react';
import { useTheme } from '../../styles';
import { Delete } from '@mui/icons-material';
import { LoadingButton } from '@common/Button';

export const SpotlightPanelContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  grid-gap: 1rem;

  &:first-of-type {
    margin-left: 0rem;
  }

  &:last-of-type {
    margin-right: 0rem;
  }
`;

const CustomCard = styled.div<{ selected?: boolean; small?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  /* min-height: 350px; */
  border: 1px solid ${({ theme, selected }) => (selected ? alpha(theme.primary, 0.75) : theme.line.light)};
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  background-color: ${({ theme }) => theme.background.standard};

  svg {
    font-size: 2.2ch;
  }

  &:hover {
    cursor: pointer;
    border-color: ${({ theme }) => alpha(theme.primary, 0.75)};

    #card-header {
      svg {
        color: ${({ theme }) => theme.primary};
      }
    }
  }

  h3 {
    padding-top: 2px;
    font-family: 'PPFormula';
    color: ${({ theme }) => theme.text.standard};
    font-size: ${({ small }) => (small ? '14px' : '14px')};
    margin: 0;
  }

  p {
    font-family: Inter;
    color: ${({ theme }) => theme.text.subtext};
    font-size: ${({ small }) => (small ? '13px' : '14px')};
    margin-bottom: 1rem;
  }

  button {
    svg {
      scale: 1;
      transition: scale 0.2s;

      &:active {
        scale: 0.9;
      }
    }
    &:hover {
      cursor: pointer;
      color: ${({ theme }) => theme.button.background.red};

      svg {
        scale: 1.1;
      }
    }
  }
`;

interface SpotlightProps {
  opacity: number;
  background: string;
}

const Spotlight = styled.div<SpotlightProps>`
  pointer-events: none;
  position: absolute;
  inset: -1px;
  opacity: 0;
  z-index: 1;
  transition: opacity 0ms;
  opacity: ${({ opacity }: { opacity: number }) => opacity};
  background: ${({ background }: { background: string }) => background};
`;

export type SpotlightCard = {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  category: string;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
  onDeleteClick?: () => void;
  loading?: boolean;
  intercomDataAttribute: string | undefined;
};

interface Props {
  card: SpotlightCard;
  intercomDataAttribute?: string | undefined;
  small?: boolean;
}

export const SpotlightPanel: React.FC<Props> = ({ card, intercomDataAttribute = undefined, small }) => {
  const divRef = useRef<ElementRef<'div'>>(null);
  const theme = useTheme();

  const [isFocused, setIsFocused] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);

  const selected = card.selected;

  const backgroundColor = alpha(theme.primary, 0.12);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current || isFocused || selected) return;

    const div = divRef.current;
    const rect = div.getBoundingClientRect();

    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => {
    setIsFocused(true);
    setOpacity(1);
  };

  const handleBlur = () => {
    setIsFocused(false);
    setOpacity(0);
  };

  const handleMouseEnter = () => {
    setOpacity(1);
  };

  const handleMouseLeave = () => {
    setOpacity(0);
  };

  return (
    <CustomCard
      onClick={card.onClick}
      selected={selected}
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className='card-spotlight-effect'
      data-intercom-target={intercomDataAttribute}
      small={small}
    >
      <Stack direction='column' width={'100%'} height={'100%'} justifyContent='space-between'>
        <Stack id='card-header' direction='column' width={'100%'}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1.2} sx={{ height: '32px' }}>
            <h3>{card.title}</h3>
            {card.icon}
          </Stack>
          <p>{card.description}</p>
        </Stack>
        {card.onDeleteClick && (
          <Stack direction='row' justifyContent='end' alignItems='center'>
            <LoadingButton loading={card.loading} onClick={card.onDeleteClick}>
              <Delete fontSize='small' />
            </LoadingButton>
          </Stack>
        )}
      </Stack>
      <Spotlight
        opacity={selected ? 1 : opacity}
        background={`radial-gradient(600px circle at ${position.x}px ${position.y}px, ${backgroundColor}, transparent 60%)`}
      />
    </CustomCard>
  );
};
